// vim: set ts=2 sts=2 sw=2 et:
//
// "Traer cambios de la web": compara el bundle fresco de la web con el meet local y propone SOLO
// cambios de una lista blanca de campos (tandas y pesaje). Nunca toca intentos juzgados, estados
// de intentos, divisiones, eventos ni equipamiento.
//
// Fusión de 3 vías por campo: `base` = el último valor de la web que esta PC procesó (aplicado o
// rechazado). Si solo cambió la web → se propone tildado. Si solo cambió la mesa → nada. Si
// cambiaron las dos → conflicto, destildado, con los dos valores. Sin base (meet cargado con un
// fork anterior), un valor local que sigue en el default del bundle cuenta como "sin tocar".
//
// Identidad del atleta: anchor (userOnTournamentId + equipamiento) → DNI + equipamiento → nombre.
// Espejo de `entryIdentityKey` (packages/api/src/openlifter/build-results-from-state.ts). Nunca el
// id de la entry: los merges y los archivos renumeran.

import type { Entry } from "../../types/dataTypes";
import type { MeetState } from "../../types/stateTypes";
import type { OpenlifterBundleFile, WrpfEntryAnchor } from "./types";
import { equipmentForOpenlifterEntry } from "./modalityMapping";

export type WebPatchField =
  | "day"
  | "platform"
  | "flight"
  | "bodyweightKg"
  | "present"
  | "weighedIn"
  | "squatOpener"
  | "benchOpener"
  | "deadliftOpener"
  | "squatRackInfo"
  | "benchRackInfo";

export type WebPatchSection = "tandas" | "pesaje";

export type FieldValue = number | string | boolean | null;

/** Último valor de la web procesado por esta PC, por identidad de atleta y campo. */
export type WebBaseline = Readonly<Record<string, Readonly<Partial<Record<WebPatchField, FieldValue>>>>>;

export const WEB_PATCH_FIELDS: ReadonlyArray<WebPatchField> = [
  "day",
  "platform",
  "flight",
  "bodyweightKg",
  "present",
  "weighedIn",
  "squatOpener",
  "benchOpener",
  "deadliftOpener",
  "squatRackInfo",
  "benchRackInfo",
];

export const FIELD_SECTION: Record<WebPatchField, WebPatchSection> = {
  day: "tandas",
  platform: "tandas",
  flight: "tandas",
  bodyweightKg: "pesaje",
  present: "pesaje",
  weighedIn: "pesaje",
  squatOpener: "pesaje",
  benchOpener: "pesaje",
  deadliftOpener: "pesaje",
  squatRackInfo: "pesaje",
  benchRackInfo: "pesaje",
};

export const FIELD_LABEL: Record<WebPatchField, string> = {
  day: "Día",
  platform: "Tarima",
  flight: "Tanda",
  bodyweightKg: "Peso corporal",
  present: "Presente",
  weighedIn: "Pesaje realizado",
  squatOpener: "Apertura sentadilla",
  benchOpener: "Apertura banca",
  deadliftOpener: "Apertura despegue",
  squatRackInfo: "Rack sentadilla",
  benchRackInfo: "Rack banca",
};

/** Valor de un campo de la lista blanca en una entry. */
export function readField(entry: Entry, field: WebPatchField): FieldValue {
  switch (field) {
    case "day":
      return entry.day;
    case "platform":
      return entry.platform;
    case "flight":
      return entry.flight;
    case "bodyweightKg":
      return entry.bodyweightKg;
    case "present":
      return entry.present !== false;
    case "weighedIn":
      return entry.weighedIn === true;
    case "squatOpener":
      return entry.squatKg[0] ?? 0;
    case "benchOpener":
      return entry.benchKg[0] ?? 0;
    case "deadliftOpener":
      return entry.deadliftKg[0] ?? 0;
    case "squatRackInfo":
      return entry.squatRackInfo ?? "";
    case "benchRackInfo":
      return entry.benchRackInfo ?? "";
  }
}

/** Default con el que el bundle crea el campo: si el local sigue ahí, nadie lo tocó en la mesa. */
function isBundleDefault(entry: Entry, field: WebPatchField): boolean {
  switch (field) {
    case "day":
    case "platform":
    case "flight":
      return entry.day === 1 && entry.platform === 1 && entry.flight === "A";
    case "bodyweightKg":
      // Sin pesaje realizado, el peso local no es un dato de la mesa: un meet cargado con un bundle
      // anterior puede traer el límite de la categoría pre-llenado.
      return entry.weighedIn !== true;
    case "present":
      return entry.present !== false;
    case "weighedIn":
      return entry.weighedIn !== true;
    case "squatOpener":
    case "benchOpener":
    case "deadliftOpener":
      return readField(entry, field) === 0;
    case "squatRackInfo":
    case "benchRackInfo":
      return readField(entry, field) === "";
  }
}

/**
 * La web tiene un peso real solo si el atleta se pesó allá. Sin eso, su peso no es un dato: un
 * bundle anterior mandaba el límite de la categoría pre-llenado y el actual manda 0.
 */
function hasWebWeighIn(entry: Entry): boolean {
  return entry.weighedIn === true && entry.bodyweightKg > 0;
}

/** Campos del pesaje en los que manda la web cuando el atleta se pesó allá. */
const WEIGH_IN_FIELDS: ReadonlySet<WebPatchField> = new Set<WebPatchField>(["bodyweightKg", "weighedIn"]);

function judgedAttempts(entry: Entry): number {
  let count = 0;
  for (const statuses of [entry.squatStatus, entry.benchStatus, entry.deadliftStatus]) {
    for (const s of statuses) if (s !== 0) count++;
  }
  return count;
}

function openerJudged(entry: Entry, field: WebPatchField): boolean {
  if (field === "squatOpener") return (entry.squatStatus[0] ?? 0) !== 0;
  if (field === "benchOpener") return (entry.benchStatus[0] ?? 0) !== 0;
  if (field === "deadliftOpener") return (entry.deadliftStatus[0] ?? 0) !== 0;
  return false;
}

const normalizeName = (name: string): string =>
  name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

export type IdentityKeys = { uot: string | null; dni: string | null; name: string | null };

/** Claves de identidad de un atleta+equipamiento, de la más fuerte (inscripción) a la más débil (nombre). */
export function identityKeys(entry: Entry, anchor: WrpfEntryAnchor | undefined): IdentityKeys {
  const equipment = equipmentForOpenlifterEntry(entry.equipment);
  const dni = entry.memberId?.trim();
  const name = normalizeName(entry.name ?? "");
  return {
    uot: anchor ? `uot:${anchor.userOnTournamentId}|${equipment}` : null,
    dni: dni ? `dni:${dni}|${equipment}` : null,
    name: name ? `name:${name}|${equipment}` : null,
  };
}

export type WebPatchRow = {
  /** Clave estable de la fila (identidad + campo). */
  key: string;
  identity: string;
  entryId: number;
  entryName: string;
  field: WebPatchField;
  section: WebPatchSection;
  local: FieldValue;
  incoming: FieldValue;
  status: "clean" | "conflict" | "blocked";
  reason: string | null;
  /** Tildada por defecto en la vista previa. */
  selected: boolean;
};

export type MeetPatch = {
  local: Pick<MeetState, "date" | "lengthDays" | "platformsOnDays">;
  incoming: Pick<MeetState, "date" | "lengthDays" | "platformsOnDays">;
};

export type WebPatch = {
  rows: WebPatchRow[];
  meet: MeetPatch | null;
  /** Atletas de la web que no están en este OpenLifter (inscriptos nuevos): solo se informan. */
  newInWeb: string[];
  /** Base nueva si se procesa todo lo propuesto (se completa con la selección al aplicar). */
  processedBaseline: Record<string, Partial<Record<WebPatchField, FieldValue>>>;
};

/** Base inicial al cargar un bundle: lo que trajo la web para cada atleta. */
export function baselineFromBundle(bundle: OpenlifterBundleFile): WebBaseline {
  const anchors = new Map((bundle._wrpf?.entryAnchors ?? []).map((a) => [a.openlifterEntryId, a]));
  const out: Record<string, Partial<Record<WebPatchField, FieldValue>>> = {};
  for (const raw of bundle.registration.entries as Entry[]) {
    const keys = identityKeys(raw, anchors.get(raw.id));
    const identity = keys.uot ?? keys.dni ?? keys.name;
    if (!identity) continue;
    const values: Partial<Record<WebPatchField, FieldValue>> = {};
    for (const field of WEB_PATCH_FIELDS) values[field] = readField(raw, field);
    out[identity] = values;
  }
  return out;
}

function sameValue(a: FieldValue | undefined, b: FieldValue | undefined): boolean {
  if (typeof a === "number" && typeof b === "number") return Math.abs(a - b) < 1e-9;
  return a === b;
}

export function buildWebPatch(args: {
  localEntries: ReadonlyArray<Entry>;
  localAnchors: ReadonlyArray<WrpfEntryAnchor>;
  localMeet: Pick<MeetState, "date" | "lengthDays" | "platformsOnDays">;
  incoming: OpenlifterBundleFile;
  baseline: WebBaseline | undefined;
}): WebPatch {
  const incomingEntries = args.incoming.registration.entries as Entry[];
  const incomingAnchors = new Map((args.incoming._wrpf?.entryAnchors ?? []).map((a) => [a.openlifterEntryId, a]));
  const localAnchors = new Map(args.localAnchors.map((a) => [a.openlifterEntryId, a]));

  // Índices de la web por cada tipo de clave; la identidad canónica es la de la web (anchor).
  type Incoming = { entry: Entry; identity: string };
  const byUot = new Map<string, Incoming>();
  const byDni = new Map<string, Incoming | null>();
  const byName = new Map<string, Incoming | null>();
  for (const entry of incomingEntries) {
    const keys = identityKeys(entry, incomingAnchors.get(entry.id));
    const identity = keys.uot ?? keys.dni ?? keys.name;
    if (!identity) continue;
    const item = { entry, identity };
    if (keys.uot) byUot.set(keys.uot, item);
    // Una clave repetida (dos entries con el mismo DNI y equipamiento) no identifica: se anula.
    if (keys.dni) byDni.set(keys.dni, byDni.has(keys.dni) ? null : item);
    if (keys.name) byName.set(keys.name, byName.has(keys.name) ? null : item);
  }

  const rows: WebPatchRow[] = [];
  const processedBaseline: Record<string, Partial<Record<WebPatchField, FieldValue>>> = {};
  const matched = new Set<string>();

  for (const local of args.localEntries) {
    const keys = identityKeys(local, localAnchors.get(local.id));
    const match =
      (keys.uot ? byUot.get(keys.uot) : undefined) ??
      (keys.dni ? byDni.get(keys.dni) : undefined) ??
      (keys.name ? byName.get(keys.name) : undefined);
    if (!match) continue;
    matched.add(match.identity);

    const base = args.baseline?.[match.identity];
    const judged = judgedAttempts(local);
    processedBaseline[match.identity] = {};

    for (const field of WEB_PATCH_FIELDS) {
      // Un peso sin pesaje en la web no se propone ni se anota en la base: así nunca borra ni pisa
      // el de la mesa, y un pre-llenado local no cuenta después como "tocado".
      if (field === "bodyweightKg" && !hasWebWeighIn(match.entry)) continue;
      const incoming = readField(match.entry, field);
      const localValue = readField(local, field);
      processedBaseline[match.identity]![field] = incoming;
      if (sameValue(incoming, localValue)) continue;

      const baseValue = base?.[field];
      const hasBase = base !== undefined && field in base;
      // Pesaje: si el atleta se pesó en la web, manda la web. Se propone tildado siempre que la mesa
      // difiera, aunque la mesa lo haya borrado, cambiado o apagado "Pesaje realizado" después de
      // traerlo. Para el resto de los campos sigue la fusión de 3 vías.
      const webWins = WEIGH_IN_FIELDS.has(field) && hasWebWeighIn(match.entry);
      const webChanged = webWins || (hasBase ? !sameValue(incoming, baseValue) : true);
      if (!webChanged) continue; // solo cambió la mesa: se respeta lo local
      const localTouched =
        !webWins && (hasBase ? !sameValue(localValue, baseValue) : !isBundleDefault(local, field));

      let status: WebPatchRow["status"] = localTouched ? "conflict" : "clean";
      let reason: string | null = localTouched ? "Cambió en la web y en esta PC" : null;

      if (FIELD_SECTION[field] === "tandas" && judged > 0) {
        status = "blocked";
        reason = "Ya tiene intentos juzgados: no se mueve de tanda";
      } else if (field === "present" && incoming === false && judged > 0) {
        status = "blocked";
        reason = "Ya levantó: no se puede marcar ausente";
      } else if (openerJudged(local, field)) {
        status = "blocked";
        reason = "El primer intento ya se juzgó";
      } else if (field === "bodyweightKg" && judged > 0) {
        // Manda la web igual; solo se avisa.
        reason = "Ya levantó: el peso nuevo puede cambiarle la categoría y el puesto";
      }

      rows.push({
        key: `${match.identity}|${field}`,
        identity: match.identity,
        entryId: local.id,
        entryName: local.name,
        field,
        section: FIELD_SECTION[field],
        local: localValue,
        incoming,
        status,
        reason,
        selected: status === "clean",
      });
    }
  }

  const newInWeb = incomingEntries
    .map((entry) => {
      const keys = identityKeys(entry, incomingAnchors.get(entry.id));
      return { entry, identity: keys.uot ?? keys.dni ?? keys.name };
    })
    .filter((x) => x.identity && !matched.has(x.identity))
    .map((x) => x.entry.name);

  const incomingMeet = args.incoming.meet as Partial<MeetState>;
  const meetIncoming = {
    date: typeof incomingMeet.date === "string" ? incomingMeet.date : args.localMeet.date,
    lengthDays: typeof incomingMeet.lengthDays === "number" ? incomingMeet.lengthDays : args.localMeet.lengthDays,
    platformsOnDays: Array.isArray(incomingMeet.platformsOnDays)
      ? incomingMeet.platformsOnDays
      : args.localMeet.platformsOnDays,
  };
  const meetDiffers =
    meetIncoming.date !== args.localMeet.date ||
    meetIncoming.lengthDays !== args.localMeet.lengthDays ||
    meetIncoming.platformsOnDays.join(",") !== args.localMeet.platformsOnDays.join(",");

  return {
    rows,
    meet: meetDiffers ? { local: args.localMeet, incoming: meetIncoming } : null,
    newInWeb,
    processedBaseline,
  };
}

/**
 * El meet que queda al aplicar: días y tarimas nunca bajan de lo que usan las entries (una entry
 * en un día que desaparece quedaría inaccesible).
 */
export function mergedMeet(
  patch: MeetPatch,
  entries: ReadonlyArray<Pick<Entry, "day" | "platform">>,
): Pick<MeetState, "date" | "lengthDays" | "platformsOnDays"> {
  const lengthDays = Math.max(patch.incoming.lengthDays, ...entries.map((e) => e.day), 1);
  const platformsOnDays = Array.from({ length: lengthDays }, (_, i) =>
    Math.max(
      patch.incoming.platformsOnDays[i] ?? 1,
      ...entries.filter((e) => e.day === i + 1).map((e) => e.platform),
      1,
    ),
  );
  return { date: patch.incoming.date, lengthDays, platformsOnDays };
}

export type WebPatchOp = { entryId: number; field: WebPatchField; value: FieldValue };

/** Operaciones de las filas elegidas; las bloqueadas nunca se aplican aunque vengan tildadas. */
export function selectedOps(patch: WebPatch, selectedKeys: ReadonlySet<string>): WebPatchOp[] {
  return patch.rows
    .filter((row) => row.status !== "blocked" && selectedKeys.has(row.key))
    .map((row) => ({ entryId: row.entryId, field: row.field, value: row.incoming }));
}

/** Aplica una operación de la lista blanca sobre una entry (lo único que "Traer cambios" escribe). */
export function applyOpToEntry(entry: Entry, op: WebPatchOp): Entry {
  const next: Entry = { ...entry };
  switch (op.field) {
    case "day":
    case "platform":
      if (typeof op.value === "number" && judgedAttempts(entry) === 0) next[op.field] = op.value;
      return next;
    case "flight":
      if (typeof op.value === "string" && judgedAttempts(entry) === 0) {
        next.flight = op.value as Entry["flight"];
      }
      return next;
    case "bodyweightKg":
      if (typeof op.value === "number") next.bodyweightKg = op.value;
      return next;
    case "present":
      if (op.value === false && judgedAttempts(entry) > 0) return next;
      next.present = op.value !== false;
      return next;
    case "weighedIn":
      next.weighedIn = op.value === true;
      return next;
    case "squatOpener":
    case "benchOpener":
    case "deadliftOpener": {
      if (typeof op.value !== "number" || openerJudged(entry, op.field)) return next;
      const key = op.field === "squatOpener" ? "squatKg" : op.field === "benchOpener" ? "benchKg" : "deadliftKg";
      const attempts = [...next[key]];
      attempts[0] = op.value;
      next[key] = attempts;
      return next;
    }
    case "squatRackInfo":
    case "benchRackInfo":
      next[op.field] = typeof op.value === "string" ? op.value : "";
      return next;
  }
}
